import { ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import React from "react"
import type { DeepReadonlyObject } from "@/context/workspace/types"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * A utility function to create a memoized component with performance tracking
 * This helps identify components that are rendering too frequently
 */
export function createMemoizedComponent<P extends object>(
  Component: React.ComponentType<P>,
  name: string,
  customCompare?: (prevProps: Readonly<P>, nextProps: Readonly<P>) => boolean
) {
  // In development, we can track component render frequency
  if (process.env.NODE_ENV === 'development') {
    const MemoizedComponent = React.memo(
      (props: P) => {
        const renderCount = React.useRef(0)
        renderCount.current += 1

        // Log excessive renders to help identify problematic components
        if (renderCount.current > 5) {
          console.warn(`Component ${name} has rendered ${renderCount.current} times. Consider reviewing its usage.`)
        }

        return React.createElement(Component, props)
      },
      customCompare
    )

    MemoizedComponent.displayName = `Memoized(${name})`
    return MemoizedComponent
  }

  // In production, just use memo without the tracking overhead
  const MemoizedComponent = React.memo(Component, customCompare)
  MemoizedComponent.displayName = `Memoized(${name})`
  return MemoizedComponent
}

/**
 * A utility function to defer expensive operations outside the critical rendering path
 */
export function deferOperation<T>(operation: () => T, callback: (result: T) => void) {
  setTimeout(() => {
    const result = operation()
    callback(result)
  }, 0)
}

/**
 * A hook to debounce expensive operations that might be triggered by events
 */
export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 300
): (...args: Parameters<T>) => void {
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  return React.useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args)
      }, delay)
    },
    [callback, delay]
  )
}

/**
 * A hook to prevent event handlers from running too frequently
 */
export function useThrottledCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 100
): (...args: Parameters<T>) => void {
  const lastCallTimeRef = React.useRef<number>(0)

  return React.useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now()

      if (now - lastCallTimeRef.current >= delay) {
        lastCallTimeRef.current = now
        callback(...args)
      }
    },
    [callback, delay]
  )
}

/**
 * Formats bytes into a human-readable string with appropriate units
 * @param bytes Number of bytes
 * @param decimals Number of decimal places to show
 * @returns Formatted string (e.g., "1.5 KB")
 */
export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
}


export function clone<T>(obj: T | DeepReadonlyObject<T>): T {
  try {
    return JSON.parse(JSON.stringify(obj)) as any;
  } catch (error) {
    console.error("clone error", { error, obj })
    return Object.assign({}, obj) as any;
  }
}



const decoder = new TextDecoder('utf-8'); // Use 'utf-8' or other encoding if needed
export function arrayBufferToString(buffer: ArrayBuffer | SharedArrayBuffer | ArrayBufferLike): string {
  if (buffer instanceof ArrayBuffer || buffer instanceof SharedArrayBuffer) {
    return decoder.decode(buffer as ArrayBuffer);
  }
  return "";
}

const encoder = new TextEncoder();
export function stringToArrayBuffer(str: string): ArrayBuffer {
  return encoder.encode(str)?.buffer as ArrayBuffer;
}


import JSON5 from 'json5';
export function parseJSON(str: string) {
  try {
    return JSON.parse(str);
  } catch (error) {
    console.warn("fallback to json5", { error, str })
    return JSON5.parse(str);
  }
}